import React, { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'

const { FiDownload, FiSettings, FiRotateCw } = FiIcons

const MockupGenerator = ({ coverImage, template, onDownload }) => {
  const [settings, setSettings] = useState({
    backgroundColor: '#f8f9fa',
    shadow: true,
    reflection: false,
    rotation: 0,
    scale: 1,
    brightness: 100,
    contrast: 100,
    saturation: 100
  })
  const [generating, setGenerating] = useState(false)
  const mockupRef = useRef(null)

  const generateMockup = async (format = 'png', quality = 'high') => {
    if (!mockupRef.current) return

    setGenerating(true)
    try {
      const canvas = await html2canvas(mockupRef.current, {
        scale: quality === 'high' ? 3 : 2,
        backgroundColor: settings.backgroundColor,
        useCORS: true,
        allowTaint: true
      })

      if (format === 'pdf') {
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [canvas.width, canvas.height]
        })
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, canvas.width, canvas.height)
        pdf.save(`mockup-${template?.name || 'book'}-${Date.now()}.pdf`)
      } else {
        const link = document.createElement('a')
        link.download = `mockup-${template?.name || 'book'}-${Date.now()}.${format}`
        link.href = canvas.toDataURL(`image/${format}`)
        link.click()
      }

      toast.success(`Mockup downloaded as ${format.toUpperCase()}`)
      onDownload && onDownload()
    } catch (error) {
      console.error('Error generating mockup:', error)
      toast.error('Failed to generate mockup')
    } finally {
      setGenerating(false)
    }
  }

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  if (!coverImage || !template) {
    return (
      <div className="bg-white rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiSettings} className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Create</h3>
        <p className="text-gray-600">Upload a cover and select a template to start generating mockups</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Mockup Preview */}
      <div className="bg-white rounded-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {template.name} Preview
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => generateMockup('png', 'high')}
              disabled={generating}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              <span>{generating ? 'Generating...' : 'Download PNG'}</span>
            </button>
            <button
              onClick={() => generateMockup('jpg', 'high')}
              disabled={generating}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              JPG
            </button>
            <button
              onClick={() => generateMockup('pdf')}
              disabled={generating}
              className="bg-accent-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-accent-700 disabled:opacity-50 transition-colors"
            >
              PDF
            </button>
          </div>
        </div>

        {/* Mockup Canvas */}
        <div 
          ref={mockupRef}
          className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-8 min-h-[400px] flex items-center justify-center"
          style={{ backgroundColor: settings.backgroundColor }}
        >
          <motion.div
            animate={{ 
              rotate: settings.rotation,
              scale: settings.scale
            }}
            className="relative"
          >
            {/* Book Cover */}
            <div 
              className={`
                relative bg-white rounded-lg overflow-hidden
                ${settings.shadow ? 'shadow-2xl' : ''}
                ${template.category === 'paperback' ? 'w-48 h-72' : ''}
                ${template.category === 'hardcover' ? 'w-52 h-76' : ''}
                ${template.category === 'ereader' ? 'w-56 h-80 bg-black p-4' : ''}
              `}
              style={{
                filter: `brightness(${settings.brightness}%) contrast(${settings.contrast}%) saturate(${settings.saturation}%)`
              }}
            >
              <img
                src={coverImage}
                alt="Book Cover"
                className="w-full h-full object-cover"
              />
              
              {/* E-reader overlay */}
              {template.category === 'ereader' && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              )}
            </div>

            {/* Reflection */}
            {settings.reflection && (
              <div 
                className="absolute top-full left-0 w-full h-1/2 opacity-30"
                style={{
                  background: `linear-gradient(to bottom, ${settings.backgroundColor}00, ${settings.backgroundColor}ff)`,
                  transform: 'scaleY(-1)',
                  maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.3), transparent)'
                }}
              >
                <img
                  src={coverImage}
                  alt="Reflection"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Settings Panel */}
      <div className="bg-white rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <SafeIcon icon={FiSettings} className="w-5 h-5" />
          <span>Customization</span>
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Color
            </label>
            <input
              type="color"
              value={settings.backgroundColor}
              onChange={(e) => updateSetting('backgroundColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-300"
            />
          </div>

          {/* Rotation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rotation: {settings.rotation}°
            </label>
            <input
              type="range"
              min="-45"
              max="45"
              value={settings.rotation}
              onChange={(e) => updateSetting('rotation', Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Scale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scale: {Math.round(settings.scale * 100)}%
            </label>
            <input
              type="range"
              min="0.5"
              max="1.5"
              step="0.1"
              value={settings.scale}
              onChange={(e) => updateSetting('scale', Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Brightness */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brightness: {settings.brightness}%
            </label>
            <input
              type="range"
              min="50"
              max="150"
              value={settings.brightness}
              onChange={(e) => updateSetting('brightness', Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Toggle Options */}
          <div className="md:col-span-2 flex flex-wrap gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={settings.shadow}
                onChange={(e) => updateSetting('shadow', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Drop Shadow</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={settings.reflection}
                onChange={(e) => updateSetting('reflection', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Reflection</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MockupGenerator